// The Hand (docs/04 §21): pure automation policies. `handStep` returns the next command the Hand
// would issue, or null when a human has to decide (deals, Reckoning intros, the Hi-Lo ladder, a
// bonus without Auto-Bonus, a draft without Auto-Draft) or a stop condition holds.
// Never automated: Propositions, Reckoning intros, Molt and ending choices.
import { B } from '../content/balance';
import { PAGE_PREFIX } from '../content/ledger';
import { SYMBOLS } from '../content/registry';
import { deckIndex, dims, passiveReel, visibleUids } from './grid';
import { holdCost } from './hunger';
import type { Modifiers } from './modifiers';
import { previewNudge } from './spin';
import { tierAtLeast } from './scoring';
import type { Command, GameEvent, GameState, HandSettings, RunState } from './types';

/** Seconds per autospin at the current H_SPEED level. */
export function handInterval(mods: Modifiers): number {
  const lvl = Math.max(0, Math.min(B.HAND_INTERVALS.length - 1, mods.hand.speed ?? 0));
  return B.HAND_INTERVALS[lvl]!;
}

export function handUnlocked(mods: Modifiers): boolean {
  return (mods.hand.autospin ?? 0) > 0;
}

/** Symbol VALUE as the Hand sees it (base + growth). */
function worth(defId: string, bonus = 0): number {
  const d = SYMBOLS.get(defId);
  if (!d) return 0;
  const roll = d.roll ? (d.roll[0] + d.roll[1]) / 2 : 0;
  return d.baseValue + roll + bonus + (d.offPattern ? 2 : 0) + (d.wild ? 6 : 0) + (d.luck ?? 0) * 2;
}

/** Auto-Hold: the reels of the best left-anchored run of 2+ matching symbols on a line. */
export function autoHoldReels(run: RunState): number[] {
  if (!run.strips.length) return [];
  const { reels, rows } = dims(run.size);
  const uids = visibleUids(run, run.stops);
  const deck = deckIndex(run);
  let best: number[] = [];
  let bestWorth = 0;
  for (let row = 0; row < rows; row++) {
    const first = deck.get(uids[0]![row]!)?.defId;
    if (!first) continue;
    let len = 1;
    while (len < reels && deck.get(uids[len]![row]!)?.defId === first) len++;
    if (len < 2 || len >= reels) continue;
    const w = worth(first) * len;
    if (w > bestWorth) {
      bestWorth = w;
      best = Array.from({ length: len }, (_, i) => i);
    }
  }
  return best;
}

/** Auto-Draft: the first priority entry that matches (id or `tag:<tag>`), else the best by worth. */
export function autoDraftIndex(options: string[], priority: string[]): number {
  const pageAt = options.findIndex((o) => o.startsWith(PAGE_PREFIX));
  if (pageAt >= 0) return pageAt;
  for (const p of priority) {
    const tag = p.startsWith('tag:') ? p.slice(4) : null;
    const i = options.findIndex((o) => (tag ? SYMBOLS.get(o)?.tags.includes(tag as never) : o === p));
    if (i >= 0) return i;
  }
  let best = 0;
  options.forEach((o, i) => worth(o) > worth(options[best]!) && (best = i));
  return best;
}

/** Auto-Feed: the lowest-worth deck symbol. */
export function autoFeedUid(run: RunState): number | null {
  if (run.deck.length <= B.MIN_DECK) return null;
  const cheapest = [...run.deck].sort((a, b) => worth(a.defId, a.bonusValue) - worth(b.defId, b.bonusValue))[0];
  return cheapest?.uid ?? null;
}

/** Auto-Nudge: the nudge with the biggest positive gain, if any. */
export function autoNudge(state: GameState, mods: Modifiers): { reel: number; dir: 1 | -1 } | null {
  const run = state.run;
  if (!run?.spin || run.charges.nudge <= 0) return null;
  let best: { reel: number; dir: 1 | -1; gain: number } | null = null;
  for (let reel = 0; reel < run.stops.length; reel++)
    for (const dir of [1, -1] as const) {
      const ev = previewNudge(state, mods, reel, dir);
      if (!ev) continue;
      const gain = ev.payout.sub(run.spin.payout).toNumber();
      if (gain > 0 && (!best || gain > best.gain)) best = { reel, dir, gain };
    }
  return best ? { reel: best.reel, dir: best.dir } : null;
}

export function handStep(state: GameState, mods: Modifiers, hand: HandSettings = state.meta.hand): Command | null {
  const run = state.run;
  if (!run || !handUnlocked(mods)) return null;
  const w = run.window;
  switch (run.phase) {
    case 'ready': {
      const night = (run.nightShift?.left ?? 0) > 0;
      if (mods.hand.autopay && w.spinsLeft > 0 && !mods.payEarlyDisabled && run.coins.gte(w.tribute.mul(hand.payAt))) return { t: 'PAY_NOW' };
      if (!night && w.spinsLeft <= 0) return null;
      if (!night && hand.stopLowSpins > 0 && w.spinsLeft <= hand.stopLowSpins) return null;
      if (mods.hand.autohold && mods.features.has('hold') && !run.held.some(Boolean)) {
        const reel = autoHoldReels(run).find((r) => !run.held[r] && !mods.lockedReels.has(r) && !passiveReel(run, r));
        if (reel !== undefined && run.charges.hold >= holdCost(run, mods) && run.stats.spins > 0) return { t: 'TOGGLE_HOLD', reel };
      }
      return { t: 'PULL', perfect: false };
    }
    case 'nudge': {
      if (mods.hand.autonudge) {
        const n = autoNudge(state, mods);
        if (n) return { t: 'NUDGE', reel: n.reel, dir: n.dir };
      }
      return { t: 'COLLECT' };
    }
    case 'bonus': {
      const mode = run.bonus?.mode;
      if (mods.hand.autobonus && mode !== 'hilo') return { t: 'BONUS_INPUT', input: { kind: 'auto' } };
      return null;
    }
    case 'gamble':
      // Only on request: saying no is never a risk taken for the player.
      return hand.declineGambles ? { t: 'GAMBLE_DECLINE' } : null;
    case 'intermission': {
      const im = run.intermission!;
      if (im.deal && !im.deal.done) return null;
      if (im.draft && !im.draft.done) {
        if (!mods.hand.autodraft) return null;
        return { t: 'DRAFT_PICK', index: autoDraftIndex(im.draft.options, hand.draftPriority) };
      }
      if (mods.hand.autofeed && mods.features.has('feed') && run.hunger >= hand.feedAt) {
        const uid = autoFeedUid(run);
        if (uid !== null) return { t: 'FEED_SYMBOL', uid };
      }
      // A Reckoning intro is never automated: the human walks into it.
      if (im.preview.some((p) => p.n === w.n + 1)) return null;
      return { t: 'CONTINUE' };
    }
    default:
      return null;
  }
}

/** Events that make the Hand let go after a step (docs/04 §21: configurable stops). */
export function handShouldStop(events: GameEvent[], hand: HandSettings, mods: Modifiers): boolean {
  for (const e of events) {
    if (e.e === 'CONSUMED' || e.e === 'DEAL_OFFER' || e.e === 'MOLTED') return true;
    if (e.e === 'GAMBLE_OFFER' && !hand.declineGambles) return true;
    if (e.e === 'BONUS_TRIGGER' && hand.stopOnBonus && !mods.hand.autobonus) return true;
    if (e.e === 'BONUS_TRIGGER' && e.mode === 'hilo') return true;
    if (e.e === 'SPIN_RESOLVED' && hand.stopOnBig && tierAtLeast(e.snapshot.tier, 'big')) return true;
  }
  return false;
}
